import { css } from 'aphrodite';
import { inject } from 'mobx-react';
import { useState } from 'react';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../../models';
import { buildDateWindowFilter, useVolunteerActivitiesQuery, VolunteerSortField } from '../../../../queries/Volunteer';
import { BreadcrumbBar } from '../../../components-v2/BreadcrumbBar';
import { DateInput } from '../../../components-v2/DateInput';
import { Column, Page } from '../../../components-v2/PageLayout';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components-v2/Select';
import { Button } from '../../../components-v2/Button';
import { Databoard, DataboardFilters, DataboardNoResults } from '../../../components/Databoard';
import { MultiContainerHeader } from '../../../components/MultiContainerHeader';
import { ROUTES } from '../../../constants/routes';
import { VolunteerSortSelect } from '../../Volunteers/VolunteerSortSelect';
import { VolunteersTable } from '../../Volunteers/VolunteersTable';
import { styleSheet as dbStyleSheet } from '../styles';
import { styleSheet } from './styles';
import { VolunteerSummaryStats } from './VolunteerSummaryStats';

const NO_EXCLUSION = 'none';

interface IProps extends IImpersonationContextComponentProps {
	onContactClick?: (contactId: string) => void;
}

const VolunteersBoardBase = ({ impersonationContext, onContactClick }: IProps) => {
	const [dateFrom, setDateFrom] = useState('');
	const [dateTo, setDateTo] = useState('');
	const [excludedActivityId, setExcludedActivityId] = useState(NO_EXCLUSION);
	const [sortBy, setSortBy] = useState<VolunteerSortField>('lastActivityDate');

	const dateFilter = buildDateWindowFilter({ from: dateFrom, to: dateTo });
	const excludeActivityIds = excludedActivityId === NO_EXCLUSION ? undefined : [excludedActivityId];
	const hasFilters = !!dateFrom || !!dateTo || !!excludeActivityIds;

	const activitiesQuery = useVolunteerActivitiesQuery({ pageSize: 100, impersonationContext });
	const activities = activitiesQuery.data?.pages.flatMap(page => page.values) ?? [];
	const exclusionOptions = [
		{ value: NO_EXCLUSION, label: 'No exclusion' },
		...activities.map(activity => ({ value: activity.id, label: activity.title || 'Untitled activity' })),
	];

	const clearFilters = () => {
		setDateFrom('');
		setDateTo('');
		setExcludedActivityId(NO_EXCLUSION);
	};

	return (
		<Page>
			<MultiContainerHeader
				appBarHeader={
					<BreadcrumbBar
						items={[
							{ label: 'Data Boards', path: ROUTES.dataBoards.home },
							{ label: 'Volunteers', path: ROUTES.dataBoards.volunteers },
						]}
					/>
				}
			/>

			<Column styles={[styleSheet.container]}>
				<Databoard styles={[styleSheet.databoardContainer]}>
					<DataboardFilters styles={[styleSheet.filters]}>
						<div className={css(styleSheet.filterField)}>
							<span className={css(styleSheet.filterLabel)}>From</span>
							<DateInput value={dateFrom} onChange={setDateFrom} />
						</div>
						<div className={css(styleSheet.filterField)}>
							<span className={css(styleSheet.filterLabel)}>To</span>
							<DateInput value={dateTo} onChange={setDateTo} />
						</div>
						<div className={css(styleSheet.filterField)}>
							<span className={css(styleSheet.filterLabel)}>Exclude activity</span>
							<Select items={exclusionOptions} value={excludedActivityId} onValueChange={setExcludedActivityId}>
								<SelectTrigger styles={styleSheet.excludeActivityTrigger}>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{exclusionOptions.map(option => (
										<SelectItem key={option.value} value={option.value}>
											{option.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className={css(styleSheet.filterField)}>
							<span className={css(styleSheet.filterLabel)}>Sort by</span>
							<VolunteerSortSelect value={sortBy} onChange={setSortBy} />
						</div>

						{hasFilters ? (
							<Button variant='secondary' styles={[dbStyleSheet.clearSearchButton]} onClick={clearFilters}>
								Clear Search
							</Button>
						) : null}
					</DataboardFilters>

					{excludeActivityIds ? (
						<p className={css(styleSheet.exclusionNote)}>
							Excluding an activity narrows the list only — the stats below cover every activity.
						</p>
					) : null}

					<VolunteersTable
						filter={dateFilter}
						excludeActivityIds={excludeActivityIds}
						sortBy={sortBy}
						hasFooter
						onContactClick={onContactClick}
						impersonationContext={impersonationContext}
						emptyState={<DataboardNoResults onClearSearch={clearFilters} showClearSearch={hasFilters} />}
					/>

					<VolunteerSummaryStats filter={dateFilter} impersonationContext={impersonationContext} />
				</Databoard>
			</Column>
		</Page>
	);
};

export const VolunteersBoard = inject(ImpersonationContextKey)(VolunteersBoardBase);
